import { useTranslation } from 'react-i18next'
import { enumValueToKey } from '../helpers'
import { ConfirmationDialogs } from '../model/enums/confirmationDialogs'
import { InformationDialogs } from '../model/enums/informationDialogs'
import {
  ConfirmationDialogConfigurationStoreModel,
  openConfirmationDialog,
  configureConfirmationDialog,
  closeConfirmationDialog,
  InformationDialogConfigurationStoreModel,
  configureInformationDialog,
  openInformationDialog
} from '../redux'

export const useConfirmationDialog = () => {
  const { t } = useTranslation(['confirmDialogs', 'informationDialogs'])

  // CONFIRMATION DIALOG

  const getConfirmationDialogTranslation = (
    type: ConfirmationDialogs,
    onCloseResolvePromiseCallback: (boolean) => void,
    onCloseRejectPromiseCallback?: (boolean) => void,
    customMsg?: string
  ): ConfirmationDialogConfigurationStoreModel | undefined => {
    return {
      title: t(`confirmDialogs:${enumValueToKey(ConfirmationDialogs, type)}.title`),
      message: customMsg
        ? customMsg
        : t(`confirmDialogs:${enumValueToKey(ConfirmationDialogs, type)}.message`),
      okBtnLabel: t(`confirmDialogs:${enumValueToKey(ConfirmationDialogs, type)}.actions.ok`),
      cancelBtnLabel: t(
        `confirmDialogs:${enumValueToKey(ConfirmationDialogs, type)}.actions.cancel`
      ),
      isWaiting: false,
      onClose: onCloseResolvePromiseCallback,
      onCancel: onCloseRejectPromiseCallback
    }
  }

  const openDialog = (
    type: ConfirmationDialogs,
    onClosePromiseCallback: any,
    onCloseRejectPromiseCallback?: any,
    customMsg?: string
  ) => {
    openConfirmationDialog()
    configureConfirmationDialog(
      getConfirmationDialogTranslation(
        type,
        onClosePromiseCallback,
        onCloseRejectPromiseCallback,
        customMsg
      )
    )
  }

  const getConfirmation = (
    type: ConfirmationDialogs,
    customMsg?: string,
    cancelResponse?: boolean
  ) => {
    if (cancelResponse) {
      return new Promise<void>((onCloseResolvePromiseCallback, onCloseRejectPromiseCallback) => {
        openDialog(type, onCloseResolvePromiseCallback, onCloseRejectPromiseCallback, customMsg)
      })
    } else {
      return new Promise<void>(onCloseResolvePromiseCallback => {
        openDialog(type, onCloseResolvePromiseCallback, undefined, customMsg)
      })
    }
  }

  /////////////////////////////////////////////////////////////////////////////////////////////////////
  // INFORMATION DIALOG

  const getInformationDialogTranslation = (
    type: InformationDialogs,
    onCloseResolvePromiseCallback: (boolean) => void,
    customMsg?: string
  ): InformationDialogConfigurationStoreModel | undefined => {
    return {
      title: t(`informationDialogs:${enumValueToKey(InformationDialogs, type)}.title`),
      message: customMsg
        ? customMsg
        : t(`informationDialogs:${enumValueToKey(InformationDialogs, type)}.message`),
      okBtnLabel: t(`informationDialogs:${enumValueToKey(InformationDialogs, type)}.actions.ok`),
      onClose: onCloseResolvePromiseCallback,
      informationMode: true
    }
  }

  const openInfoDialog = (
    type: InformationDialogs,
    onClosePromiseCallback: any,
    customMsg?: string
  ) => {
    openInformationDialog(type)
    configureInformationDialog(
      getInformationDialogTranslation(type, onClosePromiseCallback, customMsg)
    )
  }

  const getInformation = (type: InformationDialogs, customMsg?: string) => {
    return new Promise<void>(onCloseResolvePromiseCallback => {
      openInfoDialog(type, onCloseResolvePromiseCallback, customMsg)
    })
  }

  /////////////////////////////////////////////////////////////////////////////////////////////////////

  const closeConfirmation = () => {
    closeConfirmationDialog()
  }

  const closeInformation = () => {
    closeConfirmationDialog()
  }

  return { getConfirmation, getInformation, closeConfirmation, closeInformation }
}
